import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import AOS from 'aos'
import Icon from '../../../../components/icons'
import Text from '../../../../elements/Typo/Text'
import { EXPERIMENT_PROGRESS_STATUS } from '../../../../_config'
import { formatUplift, KMBformatNumber, trackEvent } from '../../../../_common/functions'
import { ADVANCE_CONFIG_OPTION } from '../../../../_common/constant'
import { ReactComponent as Confidence } from '../../../../assets/svg/confidence.svg'
import EditKPIUnitButton from '../EditKPIUnit/editKPIUnitButton'
import { BodyTooltip } from '../../../../components/BodyTooltip'
import Loader from '../../../../components/loader'

export interface CardI {
    kpi_unit?: string
    kpi_name: string
    kpi_impact_value: number
    uplift: number | string
    confidence_level: number | string
    experiment_status: 'Positive_Lift' | 'Negative_Lift' | 'Inconclusive' | 'No_impact'
    methodology?: string
    kpi_trend_text?: any
}

interface Props {
    cardDetails: CardI | null
    experiment?: any
    progressBarStyle?: any
    selectedKPI: any
    literal: string
    onUpdate: () => void
    ownerAccess: boolean
    cardLoading?: boolean
}

const getStatusBackgroundColor = (status?: string, _selectedKPI?: any) => {
    if (status === EXPERIMENT_PROGRESS_STATUS.POSITIVE_LIFT) return '#2EA87C'
    if (status === EXPERIMENT_PROGRESS_STATUS.NEGATIVE_LIFT) return '#921A28'
    if (status === EXPERIMENT_PROGRESS_STATUS.NOIMPACT) return '#921A28'
    return '#c9b802'
}

const HeaderCards: React.FC<Props> = ({ cardDetails = null, experiment, selectedKPI, literal, onUpdate, ownerAccess, progressBarStyle, cardLoading }) => {
    const { t } = useTranslation()
    const [selectedDidMethodoly, setSelectedDidMethodoly] = useState<boolean>(false)

    useEffect(() => {
        AOS.init({
            once: false, // Animation occurs every time you scroll to the element
        })
        AOS.refresh()
    }, [])

    const [kpiUnit, setKpiUnit] = useState<string>('')
    const [confidence, setConfidence] = useState<string>('')

    useEffect(() => {
        if (cardDetails && experiment) {
            setKpiUnit(cardDetails.kpi_unit || '')
            if (Number(Number(cardDetails?.confidence_level).toFixed(2)) >= 100) {
                setConfidence('99.9%')
            } else {
                setConfidence((cardDetails?.confidence_level ? Number(cardDetails?.confidence_level).toFixed(2) : 0) + '%')
            }
        }
        const isDidMethodology = cardDetails?.methodology?.toLowerCase() === ADVANCE_CONFIG_OPTION.DID.value
        setSelectedDidMethodoly(isDidMethodology)
    }, [cardDetails, experiment])

    const cardIcon = () => {
        let iconName: any = null

        if (cardDetails?.experiment_status === EXPERIMENT_PROGRESS_STATUS.POSITIVE_LIFT) {
            iconName = 'upFilled'
        } else if (cardDetails?.experiment_status === EXPERIMENT_PROGRESS_STATUS.NEGATIVE_LIFT) {
            iconName = 'downFilled'
        }

        return iconName ? <Icon name={iconName} className="text-white" /> : null
    }

    return (
        <div className={`grid grid-cols-3 gap-3 ${cardDetails?.kpi_name == '' ? 'opacity-0' : ''}`} data-aos="zoom-in" data-aos-delay="200">
            {cardLoading && (
                <div className="col-span-3 flex items-center justify-center h-[4.518rem]">
                    <Loader />
                </div>
            )}

            {!cardLoading && (
                <>
                    <div className="relative flex items-center bg-[#F5E003] rounded-[0.557rem] py-[0.625rem] px-[0.833rem] h-[4.518rem]">
                        <div className="w-full">
                            <div className="flex row items-center justify-between w-full">
                                <Text size="lg" className="!font-bold !text-[0.818rem]">
                                    {t('Uplift')}
                                </Text>
                                <div className="flex gap-2">
                                    {cardDetails?.kpi_trend_text && (
                                        <BodyTooltip text={cardDetails?.kpi_trend_text} position="top" className="flex items-center gap-1 w-full">
                                            <button
                                                type="button"
                                                className="inline-flex items-center bg-transparent border-none p-0 cursor-pointer"
                                                onClick={() => trackEvent('experiment_details_kpi_trend_info_clicked', { experiment_literal: literal, kpi_name: selectedKPI?.label, trend_text: cardDetails?.kpi_trend_text })}
                                            >
                                                <Icon name="question-mark" size={'0.9'} />
                                            </button>
                                        </BodyTooltip>
                                    )}
                                    <div
                                        className={`flex flex-row rounded-[1.8rem] items-center py-[0.05rem] px-[0.2rem] ml-3 ${(cardDetails?.experiment_status?.split('_')?.join(' ')?.length || 0) < 20 ? 'w-fit' : 'w-auto'}`}
                                        style={{ backgroundColor: getStatusBackgroundColor(cardDetails?.experiment_status, selectedKPI) }}
                                    >
                                        <Text
                                            size="sm"
                                            title={cardDetails?.experiment_status?.split('_').join(' ') || '-'}
                                            className={`text-nowrap capitalize truncate ${cardDetails?.experiment_status == EXPERIMENT_PROGRESS_STATUS.POSITIVE_LIFT || cardDetails?.experiment_status == EXPERIMENT_PROGRESS_STATUS.NEGATIVE_LIFT || cardDetails?.experiment_status == EXPERIMENT_PROGRESS_STATUS.NOIMPACT ? 'text-white' : 'text-black'}`}
                                        >
                                            {cardDetails?.experiment_status?.split('_').join(' ') || '-'}
                                        </Text>
                                        {cardIcon()}
                                    </div>
                                </div>
                            </div>

                            <Text className="text-[1.333rem] font-bold truncate !leading-none mt-1.5" title={cardDetails?.uplift != null ? formatUplift(Number(cardDetails.uplift)) : '0%'}>
                                {cardDetails?.uplift == null ? t('Not Applicable') : `${formatUplift(Number(cardDetails.uplift))}`}
                                {cardDetails?.experiment_status == EXPERIMENT_PROGRESS_STATUS.NOIMPACT ? '*' : ''}
                            </Text>
                        </div>
                    </div>
                    <div className="relative flex items-center bg-[#F5E003] rounded-[0.557rem] py-[0.625rem] px-[0.833rem] h-[4.518rem]">
                        <div className="w-full">
                            <p className="font-bold text-black flex items-center gap-2">
                                <span className="text[0.75rem]">{selectedDidMethodoly ? t('Average KPI Impact') : t('KPI Impact')}</span>
                            </p>
                            <p className="text-[0.583rem] leading-none font-bold">({cardDetails?.kpi_name || '-'})</p>
                            <p className="font-bold text-black text-[1.333rem] flex items-center gap-2 mt-1.5">
                                <span style={{ fontSize: Math.min(Math.max(0.8, 1.333 - ((KMBformatNumber(cardDetails?.kpi_impact_value) + kpiUnit).length - 10) * 0.0833), 1.333) + 'rem' }}>
                                    {cardDetails?.kpi_impact_value == null ? t('Not Applicable') : KMBformatNumber(cardDetails?.kpi_impact_value)}
                                </span>
                                {cardDetails?.kpi_impact_value != null && kpiUnit ? <span>{kpiUnit}</span> : null}
                                {cardDetails?.experiment_status == EXPERIMENT_PROGRESS_STATUS.NOIMPACT ? <span className="-ml-2">*</span> : null}
                            </p>
                        </div>
                        {ownerAccess && <EditKPIUnitButton literal={literal} KPI={selectedKPI} onUpdate={onUpdate} />}
                    </div>
                    <div className="relative flex items-center bg-[#F5E003] rounded-[0.557rem] py-[0.625rem] px-[0.833rem] h-[4.518rem]">
                        <div className="w-4/4">
                            <Text size="lg" className="!font-bold !text-[0.75rem] ">
                                {t('Confidence')}
                            </Text>
                            <Text className="text-[1.333rem] font-bold truncate !leading-none mt-1.5" title={confidence}>
                                {confidence}
                            </Text>
                        </div>
                        <Confidence />
                    </div>
                    {cardDetails?.experiment_status == EXPERIMENT_PROGRESS_STATUS.NOIMPACT ? (
                        <div className="-mt-2 italic col-span-2 text-[0.458rem] text-gray-500 leading-[1.35] font-semibold">
                            <p>*{t('We have {{confidence}} confidence that there is no meaningful impact due to the intervention.', { confidence })}</p>
                            <p>*{t('Uplift/KPI Impact shown here is not statistically significant. The increase/decline could be due to random chance.')}</p>
                        </div>
                    ) : null}
                </>
            )}
        </div>
    )
}
export default HeaderCards
